/**
 * CoverageEditDialog.js
 * Troca a cobertura de um agendamento existente para Convênio: escolhe (ou
 * cadastra na hora) a carteirinha da pessoa e devolve ao painel, que salva
 * pelo UpdateAppointment com todos os campos do agendamento.
 * Mesmo mini-fluxo do bloco "Cobertura" do drawer (CoveragePicker).
 */

import React, { useEffect, useState } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import ConfirmDialog from '../../../../../components/ConfirmDialog/ConfirmDialog';
import CoveragePicker from '../AppointmentDrawer/CoveragePicker';
import usePatientInsurances from '../AppointmentDrawer/usePatientInsurances';
import { pickDefaultCard } from '../AppointmentDrawer/CoverageSection';
import { INSURANCE_CARD_REQUIRED_ERROR } from '../../utils/billingType';

// O ConfirmDialog fica em z-index 9999; os menus MUI do formulário da carteirinha
// (Popover, 1300 por padrão) abririam atrás dele. Só dentro deste diálogo, sobem.
const aboveConfirmDialog = (outer) => ({ ...outer, zIndex: { ...(outer?.zIndex || {}), modal: 10001 } });

/**
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {number|string} props.patientId
 * @param {Array} props.providers - CompanyConfig.InsuranceProviders
 * @param {number|string|null} props.currentCardId - carteirinha atual do agendamento
 * @param {boolean} props.saving
 * @param {string} [props.error] - erro devolvido pelo backend
 * @param {Function} props.onConfirm - (card) => void
 * @param {Function} props.onCancel
 */
export default function CoverageEditDialog({
  isOpen, patientId, providers, currentCardId, saving, error, onConfirm, onCancel,
}) {
  const { activeCards, lastCoverage, loading, loadedKey, addCard } = usePatientInsurances(patientId, isOpen);
  const [cardId, setCardId] = useState(null);
  const [localError, setLocalError] = useState(null);

  // A cada abertura: a carteirinha atual, senão a sugerida (última usada, senão a primeira)
  useEffect(() => {
    if (!isOpen) return;
    setLocalError(null);
    const current = currentCardId != null
      ? activeCards.find(c => String(c.id) === String(currentCardId))
      : null;
    const suggested = current || pickDefaultCard(activeCards, lastCoverage);
    setCardId(suggested ? suggested.id : null);
  }, [isOpen, loadedKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleConfirm = () => {
    const card = activeCards.find(c => String(c.id) === String(cardId));
    if (!card) {
      setLocalError(INSURANCE_CARD_REQUIRED_ERROR);
      return;
    }
    onConfirm?.(card);
  };

  return (
    <ConfirmDialog
      isOpen={isOpen}
      title="Atendimento por convênio"
      variant="info"
      confirmLabel="Salvar"
      onConfirm={handleConfirm}
      onCancel={() => { if (!saving) onCancel?.(); }}
      confirmDisabled={loading || !cardId}
      loading={saving}
      maxWidth={460}
    >
      <ThemeProvider theme={aboveConfirmDialog}>
      <CoveragePicker
        patientId={patientId}
        providers={providers}
        cards={activeCards}
        loading={loading}
        value={cardId}
        onChange={(id) => { setCardId(id); setLocalError(null); }}
        onCardCreated={addCard}
        error={localError || error}
        disabled={saving}
      />
      </ThemeProvider>
    </ConfirmDialog>
  );
}
